import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  ServerStackIcon,
  DevicePhoneMobileIcon,
  WifiIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useManagement } from '../context/ManagementContext';
import managementService from '../api/managementService';

interface HierarchyNode {
  id: string;
  name: string;
  type: 'customer' | 'asset' | 'device' | 'gateway';
  parentId?: string;
  children: HierarchyNode[];
  isExpanded?: boolean;
  level: number;
}

interface DraggedItem {
  id: string;
  type: 'customer' | 'asset' | 'device' | 'gateway';
  name: string;
}

interface DropZoneProps {
  id: string;
  children: React.ReactNode;
  isOver?: boolean;
  canDrop?: boolean;
}

const getEntityIcon = (type: string) => {
  switch (type) {
    case 'customer':
      return BuildingOfficeIcon;
    case 'asset':
      return ServerStackIcon;
    case 'device':
      return DevicePhoneMobileIcon;
    case 'gateway':
      return WifiIcon;
    default:
      return BuildingOfficeIcon;
  }
};

const getEntityColor = (type: string) => {
  switch (type) {
    case 'customer':
      return 'text-blue-600 bg-blue-50';
    case 'asset':
      return 'text-green-600 bg-green-50';
    case 'device':
      return 'text-purple-600 bg-purple-50';
    case 'gateway':
      return 'text-orange-600 bg-orange-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

function DropZone({ id, children, isOver, canDrop }: DropZoneProps) {
  const { setNodeRef } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${isOver && canDrop
          ? 'bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg'
          : isOver
            ? 'bg-red-50 border-2 border-red-300 border-dashed rounded-lg'
            : ''
        }`}
    >
      {children}
    </div>
  );
}

function HierarchyNodeComponent({
  node,
  onToggle,
  onSelect,
  selectedNodeId,
  dragOverNodeId,
  canDropOnNode,
}: {
  node: HierarchyNode;
  onToggle: (id: string) => void;
  onSelect: (node: HierarchyNode) => void;
  selectedNodeId?: string;
  dragOverNodeId?: string;
  canDropOnNode?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const IconComponent = getEntityIcon(node.type);
  const colorClass = getEntityColor(node.type);
  const isDropTarget = dragOverNodeId === node.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'opacity-50 z-50' : ''}`}
    >
      <DropZone id={node.id} isOver={isDropTarget} canDrop={canDropOnNode}>
        <div
          className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 ${selectedNodeId === node.id
              ? 'bg-blue-50 ring-2 ring-blue-200'
              : isDropTarget && canDropOnNode
                ? 'bg-green-50 ring-2 ring-green-200'
                : isDropTarget
                  ? 'bg-red-50 ring-2 ring-red-200'
                  : 'hover:bg-gray-100'
            }`}
          style={{ paddingLeft: `${node.level * 1.5 + 0.75}rem` }}
          onClick={() => onSelect(node)}
          {...attributes}
          {...listeners}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            className="mr-2 p-1 rounded hover:bg-gray-200 transition-colors"
          >
            {node.children.length > 0 ? (
              node.isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
              )
            ) : (
              <div className="h-4 w-4" />
            )}
          </button>

          <div className={`flex items-center justify-center h-8 w-8 rounded-lg ${colorClass} mr-3 transition-all duration-200 ${isDropTarget && canDropOnNode ? 'shadow-lg scale-110' : ''
            }`}>
            <IconComponent className="h-5 w-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-900 truncate">{node.name}</span>
              <span className="ml-2 text-xs text-gray-500 capitalize">{node.type}</span>
            </div>
          </div>

          {isDropTarget && canDropOnNode && (
            <div className="ml-2 text-xs text-green-600 font-medium">
              Drop here
            </div>
          )}
          {isDropTarget && !canDropOnNode && (
            <div className="ml-2 text-xs text-red-600 font-medium">
              Cannot drop
            </div>
          )}
        </div>
      </DropZone>

      {node.isExpanded && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <HierarchyNodeComponent
              key={child.id}
              node={child}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedNodeId={selectedNodeId}
              dragOverNodeId={dragOverNodeId}
              canDropOnNode={canDropOnNode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function HierarchyView() {
  const { state, dispatch } = useManagement();
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string>();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);
  const [canDropOnCurrentNode, setCanDropOnCurrentNode] = useState(false);
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const { isLoading, error } = useQuery({
    queryKey: ['hierarchy'],
    queryFn: () => managementService.management.getHierarchy(),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => managementService.customer.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['assets'],
    queryFn: () => managementService.asset.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: () => managementService.device.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: gateways = [] } = useQuery({
    queryKey: ['gateways'],
    queryFn: () => managementService.gateway.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  // Mutations for drag and drop operations
  const updateCustomerMutation = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      managementService.customer.setParent(id, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const updateAssetMutation = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      managementService.asset.setParent(id, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });

  const updateDeviceAssetMutation = useMutation({
    mutationFn: ({ id, assetId }: { id: string; assetId: string | null }) =>
      managementService.device.setAsset(id, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const updateGatewayAssetMutation = useMutation({
    mutationFn: ({ id, assetId }: { id: string; assetId: string | null }) =>
      managementService.gateway.setAsset(id, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
    },
  });

  const buildHierarchy = (): HierarchyNode[] => {
    const allEntities = [
      ...customers.map(c => ({ ...c, type: 'customer' as const })),
      ...assets.map(a => ({ ...a, type: 'asset' as const, name: a.label })),
      ...devices.map(d => ({ ...d, type: 'device' as const })),
      ...gateways.map(g => ({ ...g, type: 'gateway' as const })),
    ];

    const entityMap = new Map<string, HierarchyNode>();
    const roots: HierarchyNode[] = [];

    // Create nodes
    allEntities.forEach(entity => {
      const getParentId = () => {
        if (entity.type === 'customer') return entity.parentId;
        if (entity.type === 'asset') return entity.parentId || entity.customerId;
        if (entity.type === 'device') return entity.assetId || entity.customerId;
        if (entity.type === 'gateway') return entity.assetId || entity.customerId;
        return undefined;
      };

      entityMap.set(entity.id, {
        id: entity.id,
        name: entity.name || (entity as any).label || entity.id,
        type: entity.type,
        parentId: getParentId(),
        children: [],
        isExpanded: expandedNodes.has(entity.id),
        level: 0,
      });
    });

    // Build hierarchy
    entityMap.forEach(node => {
      if (node.parentId && entityMap.has(node.parentId)) {
        const parent = entityMap.get(node.parentId)!;
        parent.children.push(node);
        node.level = parent.level + 1;
      } else {
        roots.push(node);
      }
    });

    // Sort children by type and name
    const sortChildren = (nodes: HierarchyNode[]) => {
      nodes.sort((a, b) => {
        if (a.type !== b.type) {
          const typeOrder = { customer: 0, asset: 1, device: 2, gateway: 3 };
          return typeOrder[a.type] - typeOrder[b.type];
        }
        return a.name.localeCompare(b.name);
      });
      nodes.forEach(node => sortChildren(node.children));
    };

    sortChildren(roots);
    return roots;
  };

  const hierarchyNodes = buildHierarchy();

  const handleToggle = (id: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelect = (node: HierarchyNode) => {
    setSelectedNodeId(node.id);
    dispatch({ type: 'SET_SELECTED_ENTITY', payload: node });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const draggedNode = findNodeById(hierarchyNodes, active.id.toString());
    if (draggedNode) {
      setDraggedItem({
        id: draggedNode.id,
        type: draggedNode.type,
        name: draggedNode.name,
      });
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over || !active) {
      setDragOverNodeId(null);
      setCanDropOnCurrentNode(false);
      return;
    }

    const draggedNode = findNodeById(hierarchyNodes, active.id.toString());
    const targetNode = findNodeById(hierarchyNodes, over.id.toString());

    if (!draggedNode || !targetNode) {
      setDragOverNodeId(null);
      setCanDropOnCurrentNode(false);
      return;
    }

    const canDrop = canDropOn(draggedNode, targetNode);
    setDragOverNodeId(over.id.toString());
    setCanDropOnCurrentNode(canDrop);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedItem(null);
    setDragOverNodeId(null);
    setCanDropOnCurrentNode(false);

    if (!over || active.id === over.id) return;

    const draggedNode = findNodeById(hierarchyNodes, active.id.toString());
    const targetNode = findNodeById(hierarchyNodes, over.id.toString());

    if (!draggedNode || !targetNode) return;

    // Determine if the drop is valid
    const isValidDrop = canDropOn(draggedNode, targetNode);
    if (!isValidDrop) return;

    // Handle the drop based on entity types
    handleEntityDrop(draggedNode, targetNode);
  };

  const findNodeById = (nodes: HierarchyNode[], id: string): HierarchyNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
    return null;
  };

  const canDropOn = (draggedNode: HierarchyNode, targetNode: HierarchyNode): boolean => {
    // Customer can be dropped on another customer (parent-child relationship)
    if (draggedNode.type === 'customer' && targetNode.type === 'customer') {
      return draggedNode.id !== targetNode.id;
    }

    // Asset can be dropped on customer or another asset
    if (draggedNode.type === 'asset' && (targetNode.type === 'customer' || targetNode.type === 'asset')) {
      return true;
    }

    // Device can be dropped on asset or customer
    if (draggedNode.type === 'device' && (targetNode.type === 'asset' || targetNode.type === 'customer')) {
      return true;
    }

    // Gateway can be dropped on asset or customer
    if (draggedNode.type === 'gateway' && (targetNode.type === 'asset' || targetNode.type === 'customer')) {
      return true;
    }

    return false;
  };

  const handleEntityDrop = (draggedNode: HierarchyNode, targetNode: HierarchyNode) => {
    if (draggedNode.type === 'customer' && targetNode.type === 'customer') {
      updateCustomerMutation.mutate({ id: draggedNode.id, parentId: targetNode.id });
    } else if (draggedNode.type === 'asset' && targetNode.type === 'customer') {
      // Set asset customer
      managementService.asset.setCustomer(draggedNode.id, targetNode.id);
    } else if (draggedNode.type === 'asset' && targetNode.type === 'asset') {
      updateAssetMutation.mutate({ id: draggedNode.id, parentId: targetNode.id });
    } else if (draggedNode.type === 'device' && targetNode.type === 'asset') {
      updateDeviceAssetMutation.mutate({ id: draggedNode.id, assetId: targetNode.id });
    } else if (draggedNode.type === 'device' && targetNode.type === 'customer') {
      managementService.device.setCustomer(draggedNode.id, targetNode.id);
    } else if (draggedNode.type === 'gateway' && targetNode.type === 'asset') {
      updateGatewayAssetMutation.mutate({ id: draggedNode.id, assetId: targetNode.id });
    } else if (draggedNode.type === 'gateway' && targetNode.type === 'customer') {
      managementService.gateway.setCustomer(draggedNode.id, targetNode.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading hierarchy</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>Unable to load the entity hierarchy. Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Entity Hierarchy</h3>
          <p className="mt-1 text-sm text-gray-500">
            Drag and drop entities to reorganize the hierarchy. Click to expand/collapse nodes.
          </p>
        </div>

        <div className="px-4 py-5 sm:p-6">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-1">
              {hierarchyNodes.length > 0 ? (
                hierarchyNodes.map(node => (
                  <HierarchyNodeComponent
                    key={node.id}
                    node={node}
                    onToggle={handleToggle}
                    onSelect={handleSelect}
                    selectedNodeId={selectedNodeId}
                    dragOverNodeId={dragOverNodeId || undefined}
                    canDropOnNode={canDropOnCurrentNode}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No entities found. Create some customers, assets, devices, or gateways to see the hierarchy.</p>
                </div>
              )}
            </div>

            <DragOverlay>
              {draggedItem && (
                <div className="bg-white border-2 border-blue-400 rounded-lg p-3 shadow-2xl transform rotate-3 scale-105 transition-transform">
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center h-8 w-8 rounded-lg ${getEntityColor(draggedItem.type)} mr-3 shadow-md`}>
                      {React.createElement(getEntityIcon(draggedItem.type), { className: 'h-5 w-5' })}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{draggedItem.name}</span>
                      <div className="text-xs text-gray-500 capitalize">{draggedItem.type}</div>
                    </div>
                  </div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {state.selectedEntity && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">Selected Entity</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <div className={`flex items-center justify-center h-12 w-12 rounded-lg ${getEntityColor(state.selectedEntity.type)} mr-4`}>
                {React.createElement(getEntityIcon(state.selectedEntity.type), { className: 'h-6 w-6' })}
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">{state.selectedEntity.name}</h4>
                <p className="text-sm text-gray-500 capitalize">{state.selectedEntity.type}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{state.selectedEntity.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">{state.selectedEntity.type}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Children</dt>
                <dd className="mt-1 text-sm text-gray-900">{state.selectedEntity.children?.length || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Level</dt>
                <dd className="mt-1 text-sm text-gray-900">{state.selectedEntity.level || 0}</dd>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
